import { requireEnv } from '@/lib/env'

/**
 * Booking-host domain model — the single source of truth for tenant booking subdomains.
 *
 * Every tenant gets `{tenantCode}.{BOOKING_BASE_DOMAIN}` for their white-labeled booking
 * site even before (or instead of) verifying a custom domain. Historically this was
 * hardcoded as `.book.taskyinn.com` (served by Vercel); the self-hosted ingress serves
 * `.book.taskyinn.sa` (and `book-stg.` on staging). URL BUILDING always uses the canonical
 * env value; HOST MATCHING accepts the legacy bases too so links printed under the old
 * domain keep routing during/after the migration.
 *
 * Client-safe: pure string logic + one NEXT_PUBLIC env read (statically referenced so
 * Next.js inlines it — see lib/env.ts). Imported by proxy.ts (edge), hooks, and pages.
 */

/** Canonical base for building booking-subdomain URLs, e.g. `book.taskyinn.sa`. */
export const BOOKING_BASE_DOMAIN = requireEnv(
  process.env.NEXT_PUBLIC_BOOKING_BASE_DOMAIN,
  'NEXT_PUBLIC_BOOKING_BASE_DOMAIN'
)

/** All bases we RECOGNIZE as booking hosts (canonical + legacy). Order matters: longest first. */
export const BOOKING_BASE_DOMAINS: readonly string[] = Array.from(
  new Set([BOOKING_BASE_DOMAIN, 'book.taskyinn.sa', 'book.taskyinn.com'])
).sort((a, b) => b.length - a.length)

function stripPort(hostname: string): string {
  return (hostname.split(':')[0] ?? hostname).toLowerCase()
}

/** True when the hostname is a tenant booking subdomain on any recognized base. */
export function isBookingHost(hostname: string): boolean {
  const host = stripPort(hostname)
  return BOOKING_BASE_DOMAINS.some((base) => host.endsWith(`.${base}`))
}

/**
 * Parse `{tenant}.{base}` or `{property}.{tenant}.{base}` into its parts.
 * Returns null for hostnames that aren't booking subdomains (including the bare base).
 */
export function matchBookingSubdomain(hostname: string): {
  tenantCode: string
  propertySlug: string | null
  base: string
} | null {
  const host = stripPort(hostname)
  const base = BOOKING_BASE_DOMAINS.find((b) => host.endsWith(`.${b}`))
  if (!base) return null

  const sub = host.slice(0, -(base.length + 1))
  const parts = sub.split('.')
  const [first, second] = parts

  if (parts.length === 1 && first) {
    return { tenantCode: first, propertySlug: null, base }
  }
  if (parts.length === 2 && first && second) {
    return { tenantCode: second, propertySlug: first, base }
  }
  return null
}

/** Canonical booking-subdomain URL for a tenant (always the env-configured base). */
export function buildBookingSubdomainUrl(tenantCode: string, propertySlug?: string | null): string {
  const host = propertySlug
    ? `${propertySlug}.${tenantCode}.${BOOKING_BASE_DOMAIN}`
    : `${tenantCode}.${BOOKING_BASE_DOMAIN}`
  return `https://${host}`
}
